// Pixel maps and swatches from Alina's sprite brief (BRI-181); hexes are the art, not tokens.

import type { SpriteSwatch } from "./sprites";

/**
 * 16x16 slot maps. O outline, C coat, S coat shade, M muzzle or chest, R ear
 * (leather on dogs, inner ear elsewhere), N nose, P eye. Any other letter is a
 * fixed colour from the silhouette's `fixed`.
 */
export type PixelMap = readonly string[];

/** Overlays that repaint C, M and S pixels only: S shade, W white, A and B the patches. */
export type PatternMask = readonly string[];

export const CAT: PixelMap = [
  "...O........O...",
  "..OCO......OCO..",
  "..OCNO....ONCO..",
  "..OCCCOOOOCCCO..",
  ".OCCCCCCCCCCCCO.",
  ".OCCCCCCCCCCCCO.",
  ".OCCOPCCCCPOCCO.",
  ".OCCCCCCCCCCCCO.",
  ".OCCCCMMNMMCCCO.",
  ".OCCCMMMMMMCCCO.",
  "..OCCMMMOMMCCO..",
  "...OCCCCCCCCO...",
  "....OCCCCCCO....",
  "...OCCCCCCCCO...",
  "..OCCCMMMMCCCO..",
  "..OOOOOOOOOOOO..",
];

export const DOG_FLOPPY: PixelMap = [
  "................",
  "....OOOOOOOO....",
  "..OOCCCCCCCCOO..",
  ".OCCCCCCCCCCCCO.",
  "ORROCCCCCCCCORRO",
  "ORROCCCCCCCCORRO",
  "ORROCOPCCPOCORRO",
  "ORROCCCCCCCCORRO",
  ".OOCCCMMMMCCCOO.",
  "...OCCMMMMMCCO..",
  "...OCMMOOOOMCO..",
  "...OCMMMOOMMCO..",
  "....OCMMMMMMCO..",
  "....OCCCCCCCO...",
  "...OCCCCCCCCCO..",
  "...OOOOOOOOOOO..",
];

export const DOG_PRICK: PixelMap = [
  "..O..........O..",
  ".ORO........ORO.",
  ".ORROOOOOOOORRO.",
  ".ORRCCCCCCCCRRO.",
  ".OCCCCCCCCCCCCO.",
  ".OCCCCCCCCCCCCO.",
  ".OCCOPCCCCPOCCO.",
  ".OCCCCCCCCCCCCO.",
  ".OOCCCMMMMCCCOO.",
  "...OCCMMMMMCCO..",
  "...OCMMOOOOMCO..",
  "...OCMMMOOMMCO..",
  "....OCMMMMMMCO..",
  "....OCMMMMCCO...",
  "...OCCMMMMCCCO..",
  "...OOOOOOOOOOO..",
];

export const RABBIT: PixelMap = [
  "...OO......OO...",
  "..OCCO....OCCO..",
  "..OCRO....ORCO..",
  "..OCRO....ORCO..",
  "..OCRO....ORCO..",
  "..OCCOOOOOOCCO..",
  ".OCCCCCCCCCCCCO.",
  ".OCCCCCCCCCCCCO.",
  ".OCCCPCCCCPCCCO.",
  ".OCCCCCCCCCCCCO.",
  ".OCCCCMMMMCCCCO.",
  "..OCCCMOOMCCCO..",
  "..OCCCMMMMCCCO..",
  "...OCCCCCCCCO...",
  "..OCCCCCCCCCCO..",
  "..OOOOOOOOOOOO..",
];

export const GUINEA_PIG: PixelMap = [
  "................",
  "................",
  "................",
  "..OO........OO..",
  ".OKKOOOOOOOOKKO.",
  "OCCCCCCCCCCCCCCO",
  "OCCCCCCCCCCCCCCO",
  "OCCPCCCCCCCCPCCO",
  "OCCCCCCCCCCCCCCO",
  "OCCCCCCCCCCCCCCO",
  "OCCCCCCOOCCCCCCO",
  "OCCCCCCCCCCCCCCO",
  "OCCCCCCCCCCCCCCO",
  ".OCCCCCCCCCCCCO.",
  "..OOOOOOOOOOOO..",
  "................",
];

export const FERRET: PixelMap = [
  "................",
  "................",
  "..OO........OO..",
  ".OKKOOOOOOOOKKO.",
  ".OCCCCCCCCCCCCO.",
  "OCCCCCCCCCCCCCCO",
  "OCSSSCCCCCCSSSCO",
  "OCSPSCCCCCCSPSCO",
  "OCCSSCCCCCCSSCCO",
  ".OCCCCMMMMCCCCO.",
  "..OCCCMOOMCCCO..",
  "..OCCCMMMMCCCO..",
  "...OCCCCCCCCO...",
  "..OCCCCCCCCCCO..",
  ".OCCCCCCCCCCCCO.",
  ".OOOOOOOOOOOOOO.",
];

export const BIRD: PixelMap = [
  "................",
  "....OOOOOOOO....",
  "...OCCCCCCCCO...",
  "..OCCCCCCCCCCO..",
  "..OCCCCCCCCCCO..",
  ".OCCPCCCCCCPCCO.",
  ".OCCCCCYYCCCCCO.",
  ".OCQCCYYYYCCQCO.",
  ".OCCCCOYYOCCCCO.",
  "..OCCCCCCCCCCO..",
  ".OSSCCCCCCCCSSO.",
  ".OSSCCCCCCCCSSO.",
  ".OSSCCCCCCCCSSO.",
  "..OSSCCCCCCSSO..",
  "...OOOOOOOOOO...",
  "......O..O......",
];

export const LIZARD: PixelMap = [
  "................",
  "................",
  "...OOOOOOOOOO...",
  "..OCCSCCCCSCCO..",
  ".OCCCCCCCCCCCCO.",
  "OCMOCCCCCCCCOMCO",
  "OCOOCCCCCCCCOOCO",
  "OCCCCCCCCCCCCCCO",
  ".OCOCCCCCCCCOCO.",
  ".OCCOOOOOOOOCCO.",
  "..OCCCCCCCCCCO..",
  "...OCCCCCCCCO...",
  "..OCSCCCCCCSCO..",
  ".OCCCCSCCSCCCCO.",
  ".OCCCCCCCCCCCCO.",
  ".OOOOOOOOOOOOOO.",
];

/** Species unknown: always drawn as a ghost. */
export const PAW: PixelMap = [
  "................",
  "....OO....OO....",
  "...OCCO..OCCO...",
  "...OCCO..OCCO...",
  ".OO.OO....OO.OO.",
  "OCCO........OCCO",
  "OCCO..OOOO..OCCO",
  ".OO..OCCCCO..OO.",
  "....OCCCCCCO....",
  "...OCCCCCCCCO...",
  "...OCCCCCCCCO...",
  "...OCCCCCCCCO...",
  "....OCCCCCCO....",
  ".....OOOOOO.....",
  "................",
  "................",
];

const rows = (marks: Record<number, string>): PatternMask =>
  Array.from({ length: 16 }, (_, y) => marks[y] ?? "................");

export const CAT_PATTERNS = {
  tabby: rows({
    4: "....S......S....",
    5: ".....S....S.....",
    7: "..S..........S..",
    9: "..S..........S..",
    13: ".....S....S.....",
  }),
  bicolour: rows({
    8: "......WWWWW.....",
    9: ".....WWWWWW.....",
    10: ".....WWWWWW.....",
    11: "......WWWW......",
    12: "......WWWW......",
    13: ".....WWWWWW.....",
    14: "......WWWW......",
  }),
  calico: rows({
    3: "...AA......B....",
    4: "..AAA.....BBB...",
    5: "..AAA.....BBBB..",
    6: "..AA........B...",
    7: "............BB..",
    13: "....AA....BB....",
    14: "...A.......BB...",
  }),
  tortoiseshell: rows({
    3: "...AA...........",
    4: "..AA......AA....",
    5: "...A.....AAA....",
    7: "..A........AA...",
    9: "............A...",
    11: "....A...........",
    13: "....A.....AA....",
    14: "...AA......A....",
  }),
  point: rows({
    1: "...A........A...",
    2: "...A........A...",
    3: "...AA......AA...",
    8: "......AAAAA.....",
    9: ".....AAAAAA.....",
    10: ".....AAAAAA.....",
    14: "...AAA....AAA...",
  }),
  spotted: rows({
    4: "......A...A.....",
    7: "...A........A...",
    9: "...A........A...",
    11: "....A......A....",
    13: "......A..A......",
  }),
} as const;

const DOG_MUZZLE = (mark: string): Record<number, string> => ({
  8: `......${mark.repeat(4)}......`,
  9: `......${mark.repeat(5)}.....`,
  10: `.....${mark.repeat(2)}....${mark}....`,
  11: `.....${mark.repeat(3)}..${mark.repeat(2)}....`,
  12: `......${mark.repeat(6)}....`,
});

const BLAZE = (from: number, to: number): Record<number, string> =>
  Object.fromEntries(
    Array.from({ length: to - from + 1 }, (_, i) => [
      from + i,
      ".......WW.......",
    ]),
  );

export const DOG_FLOPPY_PATTERNS = {
  tricolour: rows({
    ...BLAZE(4, 7),
    2: ".....AAAAAA.....",
    3: "....AAAWWAAA....",
    ...DOG_MUZZLE("W"),
  }),
  bicolour: rows({
    ...BLAZE(3, 7),
    ...DOG_MUZZLE("W"),
    13: "......WWWW......",
    14: ".....WWWWW......",
  }),
  tan_points: rows({
    5: "......A..A......",
    ...DOG_MUZZLE("A"),
    13: ".......AA.......",
  }),
  merle: rows({
    2: "....S...........",
    3: ".........S......",
    4: "........S..S....",
    5: ".....S..........",
    7: "....S.....S.....",
    13: "......S....S....",
    14: ".....S...S......",
  }),
  brindle: rows({
    3: "...S......S.....",
    4: ".....S.....S....",
    5: "....S...........",
    13: ".....S...S......",
    14: "......S...S.....",
  }),
  spotted: rows({
    3: "....A......A....",
    5: "..........A.....",
    7: ".....A..........",
    13: "......A...A.....",
    14: "........A.......",
  }),
} as const;

const PRICK_MUZZLE = (mark: string): Record<number, string> => ({
  ...DOG_MUZZLE(mark),
  13: `......${mark.repeat(4)}......`,
  14: `......${mark.repeat(4)}......`,
});

export const DOG_PRICK_PATTERNS = {
  tan_points: rows({
    3: ".......AA.......",
    4: ".......AA.......",
    5: ".......AA.......",
    ...PRICK_MUZZLE("A"),
  }),
  tricolour: rows({
    ...BLAZE(4, 7),
    3: "....AAAWWAAA....",
    ...PRICK_MUZZLE("W"),
  }),
  bicolour: rows({ ...BLAZE(3, 7), ...PRICK_MUZZLE("W") }),
  merle: rows({
    3: "....S...........",
    4: "..........S.....",
    5: "......S.........",
    7: "...S.......S....",
    13: ".....S..........",
    14: "..........S.....",
  }),
  brindle: rows({
    3: ".....S..........",
    4: "..........S.....",
    5: "....S...........",
    13: ".....S..........",
    14: "....S.....S.....",
  }),
  spotted: rows({
    4: "....A......A....",
    5: ".........A......",
    7: "......A.........",
    13: ".....A..........",
    14: "..........A.....",
  }),
} as const;

export const RABBIT_PATTERNS = {
  bicolour: rows({
    6: ".......WW.......",
    7: "......WWWW......",
    8: "......WWWW......",
    9: ".....WWWWWW.....",
    10: "....WWWWWWWW....",
    11: "...WWWWWWWWWW...",
    12: "...WWWWWWWWWW...",
    13: "...WWWWWWWWW....",
    14: "..WWWWWWWWWWWW..",
  }),
  point: rows({
    1: "...AA......AA...",
    2: "...A........A...",
    3: "...A........A...",
    4: "...A........A...",
    5: "...AA......AA...",
    10: "......AAAA......",
    11: "......A..A......",
    12: "......AAAA......",
    14: "..AAA......AAA..",
  }),
} as const;

export const GUINEA_PIG_PATTERNS = {
  bicolour: rows({
    5: "....WWWWWWWW....",
    6: "....WWWWWWWW....",
    7: ".W.WWWWWWWWWW.W.",
    8: ".WWWWWWWWWWW..W.",
    9: ".WWWWWWWWWWWWWW.",
    10: ".WWWWWWWWWWWWWW.",
    11: ".WWWWWWWWWWWWWW.",
    12: ".WWWWWWWWWWWWWW.",
    13: "..WWWWWWWWWWWW..",
  }),
} as const;

export type Family = "cat" | "dog" | "rabbit" | "small" | "bird" | "lizard";

/** Colours one swatch paints: coat, its shade, muzzle, ear leather, nose. */
export interface Tone {
  coat: string;
  shade: string;
  light: string;
  dark: string;
  nose: string;
  /** As a pattern's patch, when it differs from the coat. */
  patch?: string;
}

export const TONES: Record<SpriteSwatch, Tone> = {
  black: {
    coat: "#3A3238",
    shade: "#2A2428",
    light: "#3A3238",
    dark: "#2E2622",
    nose: "#8A6A6A",
  },
  white: {
    coat: "#F5EEE2",
    shade: "#E2D4B8",
    light: "#FFFCF4",
    dark: "#E2D4B8",
    nose: "#D8A0A8",
  },
  cream: {
    coat: "#EDE1C8",
    shade: "#C9B7A4",
    light: "#FFFCF4",
    dark: "#C9A877",
    nose: "#D8A0A8",
  },
  grey: {
    coat: "#8E8A93",
    shade: "#5E5A63",
    light: "#EDE6DA",
    dark: "#5E5A63",
    nose: "#C98A8A",
  },
  silver: {
    coat: "#C7C5CC",
    shade: "#6E6A73",
    light: "#F1E9D6",
    dark: "#8E8A93",
    nose: "#C98A8A",
  },
  lilac: {
    coat: "#B7B3BC",
    shade: "#8E8A93",
    light: "#F1E9D6",
    dark: "#8E8A93",
    nose: "#C98A8A",
  },
  chocolate: {
    coat: "#8B5A2B",
    shade: "#5A3A1B",
    light: "#D2AE6A",
    dark: "#5A3A1B",
    nose: "#8A5A4A",
  },
  brown: {
    coat: "#A9763E",
    shade: "#4A2E18",
    light: "#E8D2A8",
    dark: "#5A3A1B",
    nose: "#B06A5A",
  },
  red: {
    coat: "#C0703A",
    shade: "#8B4A22",
    light: "#F0D2B0",
    dark: "#8B4A22",
    nose: "#8A5A4A",
  },
  orange: {
    coat: "#E0A24E",
    shade: "#B8762A",
    light: "#F5E6C8",
    dark: "#B8762A",
    nose: "#C0705A",
    patch: "#D98B4A",
  },
  golden: {
    coat: "#D9A85A",
    shade: "#B8863B",
    light: "#F0DDB5",
    dark: "#B8863B",
    nose: "#8A5A4A",
  },
  tan: {
    coat: "#C89A5C",
    shade: "#6B4423",
    light: "#F0DDB5",
    dark: "#6B4423",
    nose: "#B06A5A",
    patch: "#C98E4A",
  },
  fawn: {
    coat: "#D6B07A",
    shade: "#A8864E",
    light: "#F2E2C2",
    dark: "#A8864E",
    nose: "#8A5A4A",
  },
  sable: {
    coat: "#B5864A",
    shade: "#6B4A22",
    light: "#F0DDB5",
    dark: "#6B4A22",
    nose: "#8A5A4A",
  },
  green: {
    coat: "#8CC063",
    shade: "#4E7A3A",
    light: "#D8EDB0",
    dark: "#4E7A3A",
    nose: "#4E7A3A",
  },
  blue: {
    coat: "#7FB0E0",
    shade: "#3E6E9E",
    light: "#D6E8F5",
    dark: "#3E6E9E",
    nose: "#3E6E9E",
  },
  yellow: {
    coat: "#E8C35A",
    shade: "#B8962A",
    light: "#F5E6B8",
    dark: "#B8962A",
    nose: "#B8962A",
  },
};

/** Where a species' art departs from the shared swatch. */
export const FAMILY_TONES: Partial<
  Record<Family, Partial<Record<SpriteSwatch, Partial<Tone>>>>
> = {
  dog: {
    black: { coat: "#3F3834", light: "#7A6A60" },
    grey: { coat: "#B7B3BC", shade: "#4A4650", light: "#F1E9D6" },
    white: { light: "#FFFCF4", dark: "#D6C9AE" },
  },
  rabbit: {
    grey: { coat: "#A8A2A8", light: "#E6E0E6", nose: "#D8A0A8" },
  },
  small: {
    orange: { coat: "#C87E3E" },
    white: { coat: "#FFFCF4" },
    sable: { coat: "#E8D8B8", shade: "#5A3E2A", light: "#FFFCF4" },
  },
  lizard: {
    yellow: { shade: "#6B4A2A" },
  },
};

export const WHITE_MARKING = "#F5EEE2";

export const EYE_COLOURS = {
  amber: "#D4A24E",
  brown: "#B07A3A",
  green: "#A9DE7C",
  blue: "#8EC1E8",
} as const;

export const GHOST_LIGHT = "#FFFFFF";
export const GHOST_DARK = "#D6D6D6";

export const WING_OUTLINE = "#8A7A68";
export const WING_FILL = "#FFFFFF";

/** The left wing, rows 3 to 13; mirrored for the right. Its inner column tucks behind the body. */
export const WING: readonly string[] = [
  "dddd...",
  "deeeddd",
  "deeeeed",
  "ddddeed",
  "deeeeed",
  ".dddeed",
  ".deeeed",
  "..ddeed",
  "..deeed",
  "...dded",
  "....dd.",
];
export const WING_TOP = 3;
